import { useState } from "react";
import { useSearchParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Building2, Newspaper, Mail, Wrench, Send } from "lucide-react";
import { Seo } from "@/components/Seo";
import { Reveal } from "@/components/Reveal";
import { SectionHead } from "@/components/SectionHead";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ENQUIRY_TYPES = [
  "Media & Enterprise",
  "Journalists & Researchers",
  "General Enquiry",
  "Technical / Product",
];

const PRESETS = {
  media: "Media & Enterprise",
  demo: "Media & Enterprise",
  analyst: "Journalists & Researchers",
  data: "Technical / Product",
  technical: "Technical / Product",
};

const STREAMS = [
  {
    icon: Building2,
    title: "Media & Enterprise",
    body: "For white-label dashboards, embeds, data products and media partnerships.",
    email: "hello@electionanlytx.com",
    testid: "stream-media",
  },
  {
    icon: Newspaper,
    title: "Journalists & Researchers",
    body: "For platform access, data queries and analytical collaboration.",
    email: "hello@electionanlytx.com",
    testid: "stream-research",
  },
  {
    icon: Mail,
    title: "General Enquiries",
    body: "For everything else.",
    email: "hello@electionanlytx.com",
    testid: "stream-general",
  },
  {
    icon: Wrench,
    title: "Technical / Product",
    body: "For API access, data feeds, integration and technical discussions.",
    email: "hello@electionanlytx.com",
    testid: "stream-product",
  },
];

const inputClass =
  "w-full rounded-md border border-slate-300 bg-white px-4 py-3 text-sm text-slate-900 placeholder:text-slate-400 outline-none transition-colors focus:border-[#b91c1c] focus:ring-2 focus:ring-[#b91c1c]/20";

const Contact = () => {
  const [params] = useSearchParams();
  const preset = PRESETS[params.get("type")] || ENQUIRY_TYPES[0];
  const [form, setForm] = useState({
    name: "",
    email: "",
    organisation: "",
    enquiry_type: preset,
    message: "",
  });
  const [submitting, setSubmitting] = useState(false);

  const update = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));

  const onSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await axios.post(`${API}/contact`, {
        ...form,
        organisation: form.organisation || null,
      });
      toast.success("Enquiry received. Our team will respond shortly.");
      setForm({ name: "", email: "", organisation: "", enquiry_type: form.enquiry_type, message: "" });
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Could not send your enquiry. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div data-testid="contact-page">
      <Seo
        title="Contact — Request Access & Enquiries | Anlytx for Elections"
        description="Contact Anlytx for Elections for media partnerships, white-label dashboards, journalist and researcher access, or technical and data enquiries."
      />

      <section className="relative overflow-hidden bg-[#0b1322]" data-testid="contact-hero">
        <div className="absolute inset-0 grid-faint-dark opacity-70" />
        <div className="relative mx-auto max-w-4xl px-6 py-20 text-center sm:py-24">
          <Reveal>
            <p className="font-mono-data text-xs font-medium uppercase tracking-[0.24em] text-red-400">Contact</p>
            <h1 className="mt-4 font-heading text-4xl font-extrabold leading-[1.08] tracking-tight text-white sm:text-5xl text-balance">
              Let&apos;s Talk Elections, Data or Both
            </h1>
            <p className="mx-auto mt-6 max-w-2xl text-base leading-relaxed text-slate-300 md:text-lg">
              One inbox, one team. Write to us at hello@electionanlytx.com, or use the form and we&apos;ll route your
              enquiry to the right desk.
            </p>
          </Reveal>
        </div>
      </section>

      <section className="bg-white" data-testid="contact-streams">
        <div className="mx-auto max-w-7xl px-6 py-16 sm:py-20">
          <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
            {STREAMS.map((s, i) => (
              <Reveal key={s.testid} delay={i * 0.08}>
                <div
                  data-testid={s.testid}
                  className="flex h-full flex-col rounded-xl border border-slate-200 bg-[#f8fafc] p-6"
                >
                  <span className="flex h-10 w-10 items-center justify-center rounded-md bg-[#1a2332]">
                    <s.icon className="h-5 w-5 text-white" />
                  </span>
                  <h2 className="mt-5 font-heading text-lg font-semibold text-slate-900">{s.title}</h2>
                  <p className="mt-2 flex-1 text-sm leading-relaxed text-slate-600">{s.body}</p>
                  <a
                    href={`mailto:${s.email}`}
                    data-testid={`${s.testid}-email`}
                    className="mt-4 break-all text-sm font-semibold text-[#b91c1c] hover:text-[#991b1b]"
                  >
                    {s.email}
                  </a>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="border-t border-slate-200 bg-white" data-testid="contact-parent-company">
        <div className="mx-auto max-w-7xl px-6 py-16">
          <SectionHead
            eyebrow="Parent Company"
            title="SevenM Technologies Pvt. Ltd."
            sub="Anlytx for Elections is built and operated by SevenM Technologies — offices in India and the UAE."
          />
          <div className="mt-10 grid gap-6 md:grid-cols-3">
            {[
              {
                city: "Kolkata",
                lines: ["SevenM Technologies Pvt. Ltd.", "508, 5th Floor, Saltee Plaza", "1 Khudiram Bose Sarani", "Kolkata 700080, India"],
              },
              {
                city: "Bengaluru",
                lines: ["SevenM Technologies Pvt. Ltd.", "8th Floor, The Estate", "Dickenson Road", "Bengaluru 560042, India"],
              },
              {
                city: "UAE",
                lines: ["SevenM Digital FZE", "BC 1306321, C1", "Ajman Free Zone Building", "United Arab Emirates"],
              },
            ].map((o, i) => (
              <Reveal key={o.city} delay={i * 0.08}>
                <div
                  className="h-full rounded-xl border border-slate-200 bg-[#f8fafc] p-6"
                  data-testid={`office-${o.city.toLowerCase()}`}
                >
                  <p className="font-mono-data text-xs font-semibold uppercase tracking-[0.2em] text-[#b91c1c]">
                    {o.city}
                  </p>
                  <div className="mt-3 space-y-0.5">
                    {o.lines.map((l, j) => (
                      <p key={j} className={`text-sm leading-relaxed ${j === 0 ? "font-semibold text-slate-900" : "text-slate-600"}`}>
                        {l}
                      </p>
                    ))}
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
          <Reveal delay={0.15}>
            <div className="mt-10 flex flex-wrap items-center justify-center gap-4" data-testid="contact-social-links">
              <span className="font-mono-data text-[11px] uppercase tracking-[0.2em] text-slate-400">Follow us</span>
              <a
                href="https://x.com/TeamSevenM"
                target="_blank"
                rel="noopener noreferrer"
                data-testid="contact-social-x"
                className="rounded-md border border-slate-200 px-4 py-2 text-sm font-semibold text-slate-700 transition-colors hover:border-[#b91c1c]/50 hover:text-[#b91c1c]"
              >
                X (Twitter)
              </a>
              <a
                href="https://www.linkedin.com/company/sevenm-technologies-private-limited"
                target="_blank"
                rel="noopener noreferrer"
                data-testid="contact-social-linkedin"
                className="rounded-md border border-slate-200 px-4 py-2 text-sm font-semibold text-slate-700 transition-colors hover:border-[#b91c1c]/50 hover:text-[#b91c1c]"
              >
                LinkedIn
              </a>
            </div>
          </Reveal>
        </div>
      </section>

      <section id="enquiry-form" className="relative overflow-hidden bg-[#f8fafc]" data-testid="contact-form-section">
        <div className="absolute inset-0 dot-grid opacity-30" />
        <div className="relative mx-auto max-w-3xl px-6 py-16 sm:py-20">
          <SectionHead
            eyebrow="Send an Enquiry"
            title="Tell Us What You Need"
            sub="We read every message. Expect a response from the relevant desk."
          />
          <Reveal delay={0.1}>
            <form
              onSubmit={onSubmit}
              data-testid="contact-form"
              className="mt-10 space-y-5 rounded-xl border border-slate-200 bg-white p-8 shadow-[0_24px_60px_-30px_rgba(26,35,50,0.25)]"
            >
              <div className="grid gap-5 sm:grid-cols-2">
                <div>
                  <label htmlFor="contact-name" className="mb-1.5 block text-xs font-semibold uppercase tracking-wider text-slate-500">
                    Name <span className="text-[#b91c1c]">*</span>
                  </label>
                  <input
                    id="contact-name"
                    data-testid="contact-name-input"
                    required
                    value={form.name}
                    onChange={update("name")}
                    placeholder="Your full name"
                    className={inputClass}
                  />
                </div>
                <div>
                  <label htmlFor="contact-email" className="mb-1.5 block text-xs font-semibold uppercase tracking-wider text-slate-500">
                    Email <span className="text-[#b91c1c]">*</span>
                  </label>
                  <input
                    id="contact-email"
                    type="email"
                    data-testid="contact-email-input"
                    required
                    value={form.email}
                    onChange={update("email")}
                    placeholder="you@organisation.com"
                    className={inputClass}
                  />
                </div>
              </div>
              <div className="grid gap-5 sm:grid-cols-2">
                <div>
                  <label htmlFor="contact-org" className="mb-1.5 block text-xs font-semibold uppercase tracking-wider text-slate-500">
                    Organisation <span className="font-normal normal-case text-slate-400">(optional)</span>
                  </label>
                  <input
                    id="contact-org"
                    data-testid="contact-org-input"
                    value={form.organisation}
                    onChange={update("organisation")}
                    placeholder="Newsroom, institute or company"
                    className={inputClass}
                  />
                </div>
                <div>
                  <label htmlFor="contact-type" className="mb-1.5 block text-xs font-semibold uppercase tracking-wider text-slate-500">
                    Enquiry Type <span className="text-[#b91c1c]">*</span>
                  </label>
                  <select
                    id="contact-type"
                    data-testid="contact-enquiry-type"
                    value={form.enquiry_type}
                    onChange={update("enquiry_type")}
                    className={inputClass}
                  >
                    {ENQUIRY_TYPES.map((t) => (
                      <option key={t} value={t}>
                        {t}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
              <div>
                <label htmlFor="contact-message" className="mb-1.5 block text-xs font-semibold uppercase tracking-wider text-slate-500">
                  Message <span className="text-[#b91c1c]">*</span>
                </label>
                <textarea
                  id="contact-message"
                  data-testid="contact-message-input"
                  required
                  rows={6}
                  value={form.message}
                  onChange={update("message")}
                  placeholder="Tell us about your requirement — coverage, data, integration or anything else."
                  className={`${inputClass} resize-y`}
                />
              </div>
              <button
                type="submit"
                disabled={submitting}
                data-testid="contact-submit-button"
                className="group inline-flex w-full items-center justify-center gap-2 rounded-md bg-[#b91c1c] px-7 py-3.5 text-sm font-semibold text-white transition-colors duration-300 hover:bg-[#991b1b] disabled:cursor-not-allowed disabled:opacity-60 sm:w-auto"
              >
                {submitting ? "Sending..." : "Send Enquiry"}
                <Send className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
              </button>
            </form>
          </Reveal>
        </div>
      </section>
    </div>
  );
};

export default Contact;
